import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { classNames } from "../utilities/classNames";
import { tierHasFeature, tierForFeature, TIER_LABELS } from "../lib/featureLevels";

/**
 * The upgrade prompt shown over a feature a listing's tier does not include.
 *
 * This used to blur the feature and say "contact service@vinoseeker.com to find
 * out more" — a mailto dead end, while a self-serve subscription page sat built
 * and routed a few tabs away. Every gated feature is something we built and then
 * never actually offered.
 *
 * Now it names the tier that unlocks the feature and links straight to the
 * subscription page for that listing. Falls back to the old contact line only
 * when there is no listing in context to build the link from.
 */
export default function FeatureDisabled(props) {
    // `feature` is a name, `tier` is listing.subscriptionTier. This took a bit
    // and a stored bitmask until 2026-09-12; the mask was written only when a
    // tier changed, so a catalog edit never reached a listing that already
    // existed. Asking the tier removes that drift entirely.
    const { feature, tier, className, children } = props;
    const listing = useSelector((s) => s.listings.selected);
    const user = useSelector((s) => s.auth.user);

    if (tierHasFeature(tier, feature)) {
        return children;
    }

    const requiredTier = tierForFeature(feature);
    const tierLabel = requiredTier ? TIER_LABELS[requiredTier] : null;
    const basePath = user?.role === "industry" ? "/industry/listings" : "/admin/listings";
    const subscriptionUrl = listing?.id ? `${basePath}/${listing.id}/subscription` : null;

    return (
        <div className={classNames("feature-disabled relative my-2 py-2 rounded-md overflow-hidden", className)}>
            <div className="blur-sm">
                {children}
            </div>
            <div className="w-full h-full absolute bg-slate-300/75 white text-center font-bold inset-0 flex flex-col justify-center items-center px-4">
                <p>
                    {tierLabel
                        ? `Available with ${tierLabel} and above.`
                        : "This feature is only available for upgraded listings."}
                </p>
                {subscriptionUrl ? (
                    <Link
                        to={subscriptionUrl}
                        className="mt-2 inline-block rounded-md px-3 py-2 text-sm font-medium bg-red-800 text-white hover:bg-red-900"
                    >
                        See plans
                    </Link>
                ) : (
                    <p className="font-normal">
                        Please contact{" "}
                        <a href="mailto:service@vinoseeker.com" className="underline">
                            service@vinoseeker.com
                        </a>{" "}
                        to find out more.
                    </p>
                )}
            </div>
        </div>
    );
}
